// Money is ALWAYS an integer count of the currency's minor unit (cents, paise, kobo…).
// Never store or compute money as a float. Currencies with no minor unit (JPY, KRW, VND,
// CLP, UGX…) have 0 decimals, so 1 unit = 1. IDR follows ISO-4217 (2 decimals) but most IDR
// rails charge whole rupiah — see chargeIncrement().
//
// Postgres columns are BIGINT; the server installs a pg type parser that returns them as
// JS numbers (safe up to 2^53, far beyond any order total).

export type Money = number

/** ISO-4217 minor-unit exponent. Anything not listed has 2. */
const DECIMALS: Record<string, number> = {
  BIF: 0, CLP: 0, DJF: 0, GNF: 0, ISK: 0, JPY: 0, KMF: 0, KRW: 0, PYG: 0, RWF: 0,
  UGX: 0, UYI: 0, VND: 0, VUV: 0, XAF: 0, XOF: 0, XPF: 0,
  BHD: 3, IQD: 3, JOD: 3, KWD: 3, LYD: 3, OMR: 3, TND: 3,
}

export function currencyDecimals(currency: string): number {
  return DECIMALS[currency.toUpperCase()] ?? 2
}

/** Minor units -> major units as a number, for display maths only (never persist this). */
export function toMajor(amount: Money, currency: string): number {
  return amount / 10 ** currencyDecimals(currency)
}

/** Major units (e.g. user typed "19.99") -> minor units, rounded half-away-from-zero. */
export function toMinor(major: number | string, currency: string): Money {
  const n = typeof major === 'string' ? Number(major.replace(/[, _]/g, '')) : major
  if (!Number.isFinite(n)) throw new Error(`Invalid amount: ${major}`)
  const factor = 10 ** currencyDecimals(currency)
  return Math.sign(n) * Math.round(Math.abs(n) * factor + 1e-9)
}

/**
 * Convert an amount in minor units of `from` into minor units of `to`, EXACTLY (BigInt maths on the
 * rate's decimal digits, no float drift): amount × rate × 10^(decimals(to) − decimals(from)),
 * rounded half away from zero (half-up on the magnitude, so convertMinor(−x) === −convertMinor(x)).
 * `rate` = units of `to` per ONE unit of `from` ("83.1245" for USD→INR), as a decimal string
 * (Postgres numeric, parsed as text) or a number; it must be > 0. Handles 0/2/3-decimal currencies
 * (JPY, INR, KWD …). Throws on a non-integer amount, a bad rate or a result beyond 2^53.
 *   convertMinor(1999, 'USD', 'INR', '83.12') === 166157   ($19.99 → ₹1,661.57)
 *   convertMinor(1999, 'USD', 'JPY', '149.5') === 2989     ($19.99 → ¥2,989)
 */
export function convertMinor(amount: Money, from: string, to: string, rate: string | number): Money {
  if (!Number.isSafeInteger(amount)) throw new Error(`convertMinor: amount must be an integer, got ${amount}`)
  const raw = typeof rate === 'number' ? (Number.isFinite(rate) ? String(rate) : '') : String(rate ?? '').trim()
  const m = /^\+?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d{1,3}))?$/.exec(raw)
  if (!m || !(m[1] || m[2])) throw new Error(`convertMinor: invalid rate ${String(rate)}`)
  const frac = m[2] ?? ''
  const digits = BigInt((m[1] || '0') + frac)
  if (digits === 0n) throw new Error(`convertMinor: rate must be greater than 0, got ${String(rate)}`)
  // value = amount × digits × 10^k, with k = decimals(to) − decimals(from) − (frac digits − exponent)
  const k = currencyDecimals(to) - currencyDecimals(from) - (frac.length - Number(m[3] ?? 0))
  let n = BigInt(amount) * digits
  if (k >= 0) n *= 10n ** BigInt(k)
  else {
    const d = 10n ** BigInt(-k)
    const neg = n < 0n
    const abs = neg ? -n : n
    let q = abs / d
    if ((abs % d) * 2n >= d) q += 1n
    n = neg ? -q : q
  }
  if (n > BigInt(Number.MAX_SAFE_INTEGER) || n < -BigInt(Number.MAX_SAFE_INTEGER)) throw new RangeError('convertMinor: result out of range')
  return Number(n)
}

/** Format for humans, e.g. formatMoney(199900, 'INR', 'en-IN') -> "₹1,999.00". */
export function formatMoney(amount: Money, currency: string, locale = 'en'): string {
  const d = currencyDecimals(currency)
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency, minimumFractionDigits: d, maximumFractionDigits: d,
    }).format(toMajor(amount, currency))
  } catch {
    return `${currency} ${toMajor(amount, currency).toFixed(d)}`
  }
}

/** Percentage (basis points: 1500 = 15%) of an amount, rounded to the minor unit. */
export function percentOf(amount: Money, basisPoints: number): Money {
  return Math.round((amount * basisPoints) / 10000)
}

/**
 * Split `total` across `weights` so the parts sum EXACTLY to total (largest-remainder
 * method). Used to allocate an order-level discount across lines.
 */
export function allocate(total: Money, weights: number[]): Money[] {
  const sum = weights.reduce((a, b) => a + b, 0)
  if (sum <= 0) return weights.map((_, i) => (i === 0 ? total : 0))
  const raw = weights.map(w => (total * w) / sum)
  const floors = raw.map(r => Math.floor(r))
  let rest = total - floors.reduce((a, b) => a + b, 0)
  const order = raw.map((r, i) => [r - Math.floor(r), i] as const).sort((a, b) => b[0] - a[0])
  for (let k = 0; k < order.length && rest > 0; k++, rest--) floors[order[k][1]]++
  return floors
}

/** Currencies offered in store settings (code, name). Not exhaustive of ISO-4217. */
export const CURRENCIES: Array<[string, string]> = [
  ['USD', 'US Dollar'], ['EUR', 'Euro'], ['GBP', 'British Pound'], ['INR', 'Indian Rupee'],
  ['AED', 'UAE Dirham'], ['SAR', 'Saudi Riyal'], ['QAR', 'Qatari Riyal'], ['KWD', 'Kuwaiti Dinar'],
  ['BHD', 'Bahraini Dinar'], ['OMR', 'Omani Rial'], ['EGP', 'Egyptian Pound'], ['NGN', 'Nigerian Naira'],
  ['GHS', 'Ghanaian Cedi'], ['KES', 'Kenyan Shilling'], ['ZAR', 'South African Rand'], ['UGX', 'Ugandan Shilling'],
  ['TZS', 'Tanzanian Shilling'], ['RWF', 'Rwandan Franc'], ['XOF', 'West African CFA'], ['XAF', 'Central African CFA'],
  ['MAD', 'Moroccan Dirham'], ['PKR', 'Pakistani Rupee'], ['BDT', 'Bangladeshi Taka'], ['LKR', 'Sri Lankan Rupee'],
  ['NPR', 'Nepalese Rupee'], ['IDR', 'Indonesian Rupiah'], ['MYR', 'Malaysian Ringgit'], ['PHP', 'Philippine Peso'],
  ['THB', 'Thai Baht'], ['VND', 'Vietnamese Dong'], ['SGD', 'Singapore Dollar'], ['BRL', 'Brazilian Real'],
  ['MXN', 'Mexican Peso'], ['COP', 'Colombian Peso'], ['CLP', 'Chilean Peso'], ['PEN', 'Peruvian Sol'],
  ['ARS', 'Argentine Peso'], ['TRY', 'Turkish Lira'], ['CAD', 'Canadian Dollar'], ['AUD', 'Australian Dollar'],
  ['NZD', 'New Zealand Dollar'], ['JPY', 'Japanese Yen'], ['KRW', 'South Korean Won'], ['CNY', 'Chinese Yuan'],
  ['HKD', 'Hong Kong Dollar'], ['CHF', 'Swiss Franc'], ['SEK', 'Swedish Krona'], ['NOK', 'Norwegian Krone'],
  ['DKK', 'Danish Krone'], ['PLN', 'Polish Zloty'],
]

/**
 * Smallest step a gateway can charge in, in minor units. Drivers round a charge UP to this step
 * (chargedAmount ≥ the order amount), e.g. Stripe needs 3-decimal currencies to end in 0,
 * whole-rupiah rails need IDR multiples of 100, the UPI gateway charges whole rupees.
 */
export function chargeIncrement(provider: string, currency: string): number {
  const c = currency.toUpperCase()
  if (provider === 'stripe' && currencyDecimals(c) === 3) return 10
  if (c === 'IDR' && (provider === 'xendit' || provider === 'stripe')) return 100
  if (provider === 'upi_gateway' && c === 'INR') return 100
  return 1
}
export const roundUpTo = (amount: Money, step: number): Money => (step <= 1 ? amount : Math.ceil(amount / step) * step)
